import { scrubPIIFromDataLayer } from '@/analytics/scrubPIIFromAnalytics';
import type { DataLayer, PayloadItems } from '@/analytics/types';
import { EventName } from '@/analytics/types';
import { useDiscountContext } from '@/app/[locale]/store/_contexts/DiscountProvider';
import useCurrency from '@/app/hooks/useCurrency';
import logToDatadog from '@/logger/logToDatadog';
import { getCartIdCookie } from '@/utils/cartUtils';
import { getCookie } from '@/utils/cookie';

const ORDER_COMPLETE_TRACK_FIRED = 'order complete';
const BRAND = 'Oura Ring';

export interface UseProductAnalytics {
  sendProductAddedEvent: (payload: PayloadItems) => Promise<void>;
  sendProductViewedEvent: (payload: PayloadItems) => Promise<void>;
}

const useProductAnalytics = (): UseProductAnalytics => {
  const { currencyCode } = useCurrency();
  const { appliedDiscountCode } = useDiscountContext();

  // Product Viewed
  const sendProductViewedEvent = async (payload: PayloadItems) => {
    const product = payload.product;
    const id = product?.id ?? 0;
    const imageUrl = product?.lineItemImage?.originalSrc;
    const price = product?.price?.amount ?? 0;
    const discountedPrice = product?.discountedPrice.discountedPrice?.amount;
    const comparedAt = product?.discountedPrice.originalPrice?.amount;
    const discount =
      comparedAt && discountedPrice ? comparedAt - discountedPrice : '';
    const title = product?.title ?? '';
    const variants = product?.variants;
    const variant = variants?.[0];
    const variantId = variant?.id ?? '';
    const sku = variant?.sku;
    const category = product?.analytics_category ?? product?.product_type ?? '';
    const url = `https://www.ouraring.com/${payload.path?.replace(/^\/+/, '')}`;
    const fbc = getCookie('_fbc');
    const fbp = getCookie('_fbp');

    // GA4
    const ga4Data: DataLayer = {
      event: EventName.ViewItem,
      ecommerce_data: {
        items: [
          {
            item_name: title,
            item_id: id,
            price,
            item_brand: BRAND,
            item_category: category,
            item_variant: variantId,
            quantity: 1,
          },
        ],
      },
    };

    try {
      window.dataLayer?.push(ga4Data);
    } catch (error) {
      logToDatadog({
        data: {
          ...scrubPIIFromDataLayer(ga4Data),
        },
        error,
        message: 'dataLayer push failed in ga4 Service',
        module: 'analytics',
      });
    }

    // segment
    await window.ouraAnalytics.track(EventName.ProductViewed, {
      brand: BRAND,
      category,
      compared_at: comparedAt,
      coupon: appliedDiscountCode,
      currency: currencyCode,
      discount,
      fbc,
      fbp,
      image_url: imageUrl,
      name: title,
      position: 3,
      price: discountedPrice ?? price,
      product_id: id,
      quantity: 1,
      sku,
      url,
      variant: variantId,
    });
  };

  // Product added to cart
  const sendProductAddedEvent = async (payload: PayloadItems) => {
    const product = payload.product;
    const cartId = getCartIdCookie();
    const id = product?.id ?? 0;
    const imageUrl = product?.lineItemImage?.originalSrc;
    const price = product?.price?.amount ?? 0;
    const discountedPrice = product?.discountedPrice.discountedPrice?.amount;
    const comparedAt = product?.discountedPrice.originalPrice?.amount;
    const discount =
      comparedAt && discountedPrice ? comparedAt - discountedPrice : '';
    const title = product?.title ?? '';
    const variant = product?.variants[0];
    const variantId = variant?.id ?? '';
    const sku = variant?.sku;
    const category = product?.analytics_category ?? product?.product_type ?? '';
    const url = `https://www.ouraring.com/${payload.path}`;
    const fbc = getCookie('_fbc');
    const fbp = getCookie('_fbp');
    const variant_name = payload.variant_name;
    const stockStatus = payload.stockStatus;

    // remove localStorage record of any previous order completed event
    // DO NOT REMOVE THIS LINE
    localStorage.removeItem(ORDER_COMPLETE_TRACK_FIRED);

    // GA4
    const ga4Data: DataLayer = {
      event: EventName.AddToCart,
      ecommerce_data: {
        items: [
          {
            item_name: title,
            item_id: id,
            price,
            item_brand: BRAND,
            item_category: category,
            item_variant: variantId,
            quantity: 1,
          },
        ],
      },
    };

    try {
      window.dataLayer?.push(ga4Data);
    } catch (error) {
      logToDatadog({
        data: {
          ...scrubPIIFromDataLayer(ga4Data),
        },
        error,
        message: `dataLayer push failed in ga4 Service`,
        module: 'analytics',
      });
    }

    // segment
    await window.ouraAnalytics.track(EventName.ProductAdded, {
      brand: BRAND,
      cart_id: cartId,
      category,
      compared_at: comparedAt,
      coupon: appliedDiscountCode,
      currency: currencyCode,
      discount,
      fbc,
      fbp,
      image_url: imageUrl,
      name: title,
      position: 3,
      price: discountedPrice ?? price,
      product_id: id,
      quantity: 1,
      sku,
      stockStatus,
      url,
      variant_name,
      variant: variantId,
    });
  };

  return { sendProductAddedEvent, sendProductViewedEvent };
};

export default useProductAnalytics;
